/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React from "react";
import { View } from "react-native";
import { TableCell } from "@material-ui/core";
import { useMutation } from "@apollo/client";

import { useSelector, useDispatch } from "react-redux";
import { RootState } from "store/types";
import { ActionCreators } from "actions";

import { Text } from "UpduoLibrary";
import MemberRow from "./MemberRow";
import {
  skaleBlack,
  upduoMediumLightGray,
  skaleWhite,
} from "config/UpduoColors";
import { EDIT_STAFF_USER } from "graphql/internal/Mutations";
import { EditUserMutation } from "graphql/internal/generated";
import * as UpduoAnalytics from "config/UpduoAnalytics";
import MemberTableBase from "../MemberTableBase";

export default function MemberTable() {
  const dispatch = useDispatch();
  const memberUserList = useSelector(
    (state: RootState) => state.memberUserList
  );

  const [editUser, {}] = useMutation<EditUserMutation>(EDIT_STAFF_USER, {
    fetchPolicy: "no-cache",
    onError: (error) => {
      console.error("ERROR editStaffUser", error);
      UpduoAnalytics.logEvent("app-onboarding-editstaffuser-error", {
        error,
      });
    },
  });

  const _generateMemberRow = (user: any, userIndex: number) => (
    <MemberRow
      key={userIndex}
      rowData={user.node}
      userIndex={userIndex}
      changePermissions={async (permissions) => {
        let result: any = await editUser({
          variables: {
            editUserRequest: Object.assign(
              { userId: user.node.id },
              permissions
            ),
          },
        });

        result = result.data.editUser;
        if (!result.ok) {
          const error = result.reason;
          console.error("ERROR editStaffUser", error);
          UpduoAnalytics.logEvent("app-onboarding-editstaffuser-error", {
            error,
          });
          return;
        }

        delete result.ok;
        delete result.reason;

        // Make a copy of everything we're going to change, and change the copy
        const newUser = Object.assign({}, user);
        newUser.node = Object.assign({}, user.node, result);
        const edges = memberUserList.edges.slice();
        edges[userIndex] = newUser;
        const newMemberList = Object.assign({}, memberUserList, { edges });

        dispatch(ActionCreators.setMemberUserList(newMemberList));
      }}
    />
  );

  const columnHeaderArray = [
    "",
    "NAME",
    "TITLE",
    "ID",
    "EMAIL",
    "REGION",
    "DISTRICT",
    "STORE",
    "HIRE DATE",
    "LAST ACTIVE",
    "ACCOUNT CREATED",
    "COACH",
    "TRAINER",
    "PERMISSIONS",
  ];

  const columnHeaders = columnHeaderArray.map((item, itemIndex) => (
    <TableCell
      key={itemIndex + columnHeaderArray.length}
      style={{
        color: skaleBlack,
        backgroundColor: skaleWhite,
      }}
    >
      <View style={{ height: 40, justifyContent: "center" }}>
        <Text style={{ color: upduoMediumLightGray }}>{item}</Text>
      </View>
    </TableCell>
  ));

  return (
    <MemberTableBase
      headers={columnHeaders}
      body={
        memberUserList.edges
          ? memberUserList.edges?.map(_generateMemberRow)
          : null
      }
    />
  );
}
